<script lang="ts" setup>
  import { ref, computed, onMounted } from 'vue';
  import { useRoute, useRouter } from 'vue-router/composables';
  import { useStorage } from '@vueuse/core';
  import { ComputeProvisioningEligibility } from '@schema';
  import appConfig from '@/common/appConfig';
  import { useI18n } from '@/common/composables/vue-i18n.composable';
  import useThemeStore from '@/common/store/pinia/theme';
  import useComputeProvisioningStore from '@/common/store/pinia/computeProvisioning';
  import getClusterId from '@/common/store/pinia/storageCluster/getClusterId';
  import ClusterNotFoundError from '@/common/utils/errors/ClusterNotFoundError';
  import takeFirst from '@/common/utils/takeFirst';
  import ability from '@/common/ability';
  import { isEligibilityBlocked, eligibilityReasonText } from '@/common/utils/computeProvisioningEligibility';
  import { clearProvisioningTimer, trackProvisioning } from '@/common/utils/computeProvisioningTelemetry';

  const WELCOME_DIALOG_DISMISSED_STORAGE_KEY = 'computeProvisioning:welcomeDialogDismissed';

  const { t } = useI18n();
  const route = useRoute();
  const router = useRouter();
  const themeStore = useThemeStore();
  const computeProvisioningStore = useComputeProvisioningStore();

  const visible = ref(false);
  const dismissed = useStorage(WELCOME_DIALOG_DISMISSED_STORAGE_KEY, false);
  const eligibility = ref<ComputeProvisioningEligibility | null>(null);

  const isBlocked = computed(() => isEligibilityBlocked(eligibility.value));
  const reasonText = computed(() => eligibilityReasonText(eligibility.value));

  const checkShouldOffer = async (): Promise<void> => {
    if (appConfig.disable_welcome_wizard || route.name === 'infrastructureCreateComputeCluster') {
      return;
    }

    if (dismissed.value || !ability.can('create', 'compute_cluster')) {
      return;
    }

    try {
      await getClusterId();
      return;
    } catch (error) {
      if (!(error instanceof ClusterNotFoundError)) {
        return;
      }
    }

    try {
      eligibility.value = await takeFirst(computeProvisioningStore.fetchEligibility);
    } catch {
      return;
    }
    if (computeProvisioningStore.canResume) {
      return;
    }
    if (eligibility.value?.action === ComputeProvisioningEligibility.action.UNAVAILABLE) {
      return;
    }

    visible.value = true;
    clearProvisioningTimer();
    trackProvisioning('offered', { blocked: isBlocked.value, eligibility: eligibility.value?.action || null });
  };

  const handleDismiss = (): void => {
    trackProvisioning('entry_choice', { choice: 'manual' });
    visible.value = false;
    dismissed.value = true;
  };

  const handleStartWizard = (): void => {
    if (isBlocked.value) {
      return;
    }
    trackProvisioning('entry_choice', { choice: 'guided' });
    visible.value = false;
    router.push({ name: 'infrastructureCreateComputeCluster' }).catch(() => {});
  };

  onMounted(() => {
    checkShouldOffer();
  });
</script>

<template>
  <el-dialog
    v-if="visible"
    append-to-body
    class="compute-provisioning-welcome-dialog v-compute-provisioning-welcome-dialog"
    :close-on-click-modal="false"
    data-testid="compute-provisioning-welcome-dialog"
    :title="t('Welcome to {product}', { product: themeStore.themeData.productName })"
    :visible="visible"
    width-size="medium"
    @close="handleDismiss"
  >
    <div class="dialog-body pa-24">
      <p>{{ t('Let\'s set up your first cluster.') }}</p>
      <p>
        {{ t('If you plan to run virtual machines, click "Start guided setup". The guided setup takes you through network, storage, and compute configuration and sets up the cluster accordingly.') }}
      </p>
      <p>{{ t('After completing the setup, you can provision S3, NFS, and other services.') }}</p>
      <i18n
        path="For a storage-only deployment, such as S3 without compute, click &quot;Configure manually&quot; and follow the {0}."
        tag="p"
      >
        <el-link
          :href="themeStore.themeData.deploymentAndConfigurationLink"
          target="_blank"
        >
          {{ t('documentation') }}
        </el-link>
      </i18n>
      <p data-testid="compute-provisioning-welcome-restart-hint">
        {{ t('You can restart the guided setup later by clicking "{button}" on the Infrastructure > Nodes screen.', {
          button: t('Create compute cluster')
        }) }}
      </p>
      <el-alert
        v-if="isBlocked"
        card
        data-testid="compute-provisioning-welcome-reason"
        hide-close
        show-icon
        type="warning"
      >
        {{ reasonText }}
      </el-alert>
    </div>
    <template #footer>
      <el-button
        data-testid="compute-provisioning-welcome-manual-button"
        type="secondary"
        @click="handleDismiss"
      >
        {{ t('Configure manually') }}
      </el-button>
      <el-button
        data-testid="compute-provisioning-welcome-start-button"
        :disabled="isBlocked"
        type="primary"
        @click="handleStartWizard"
      >
        {{ t('Start guided setup') }}
      </el-button>
    </template>
  </el-dialog>
</template>

<style lang="scss" scoped>
  .dialog-body p {
    margin: 0 0 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }
</style>
